/**
 * Plantillas de documentos (MODELOS). Antes de usarlas con clientes tienen que ser
 * revisadas por un escribano o abogado en Uruguay. Lo que aparece como [completar]
 * no se puede autocompletar desde el CRM.
 *
 * Variables disponibles: {{fecha}} {{ciudad}} {{socia}}
 *   {{cliente.nombre}} {{cliente.email}} {{cliente.telefono}} {{cliente.pais}}
 *   {{propiedad.nombre}} {{propiedad.direccion}} {{propiedad.ciudad}} {{propiedad.precio}}
 *   {{propiedad.m2}} {{propiedad.dormitorios}} {{propiedad.tipo}}
 */
import type { Rec } from '../../shared/schema'
import { fmtDayLong, today, usd } from './format'

export interface Template { key: string; tipo: string; titulo: string; descripcion: string; cuerpo: string }

export const TEMPLATES: Template[] = [
  {
    key: 'exclusividad',
    tipo: 'Exclusividad y mediación',
    titulo: 'Contrato de exclusividad y mediación inmobiliaria',
    descripcion: 'Autorización de venta en exclusiva entre el propietario y REVELA.',
    cuerpo: `CONTRATO DE MEDIACIÓN INMOBILIARIA CON EXCLUSIVIDAD

En la ciudad de {{ciudad}}, el {{fecha}}, comparecen:

POR UNA PARTE: {{cliente.nombre}}, documento de identidad [completar], con domicilio en [completar], en adelante "LA PARTE PROPIETARIA".

POR OTRA PARTE: REVELA, representada por {{socia}}, operadora inmobiliaria, con domicilio en Paysandú, en adelante "LA INMOBILIARIA".

Y CONVIENEN:

PRIMERO (Objeto). LA PARTE PROPIETARIA encomienda a LA INMOBILIARIA, con carácter exclusivo, la mediación para la venta del inmueble {{propiedad.tipo}} ubicado en {{propiedad.direccion}}, {{propiedad.ciudad}}, padrón número [completar], con una superficie aproximada de {{propiedad.m2}} m².

SEGUNDO (Precio). El precio de venta solicitado es de {{propiedad.precio}}, pudiendo LA PARTE PROPIETARIA aceptar ofertas distintas a su exclusivo criterio.

TERCERO (Plazo). La exclusividad se otorga por [completar] días a partir de la firma. Vencido el plazo, se renovará automáticamente por períodos iguales salvo aviso escrito con [completar] días de anticipación.

CUARTO (Honorarios). Concretada la operación con un comprador presentado por LA INMOBILIARIA durante la vigencia de este contrato, LA PARTE PROPIETARIA abonará una comisión del [completar] % más IVA sobre el precio efectivamente pactado, pagadera al momento de [completar].

QUINTO (Obligaciones de LA INMOBILIARIA). Promocionar el inmueble en sus canales, coordinar y acompañar las visitas, asesorar a LA PARTE PROPIETARIA en la negociación e informar periódicamente sobre las gestiones realizadas.

SEXTO (Obligaciones de LA PARTE PROPIETARIA). Entregar la documentación del inmueble necesaria para la venta, facilitar las visitas coordinadas y no comercializar el inmueble por sí ni por terceros durante la exclusividad.

SÉPTIMO (Documentación). LA PARTE PROPIETARIA declara que el inmueble se encuentra libre de obligaciones, gravámenes y embargos, salvo: [completar].

OCTAVO (Domicilios y notificaciones). Las partes constituyen domicilio en los indicados y aceptan como válidas las notificaciones por correo electrónico: [completar].

Para constancia se firman dos ejemplares de igual tenor en el lugar y fecha indicados.


______________________________          ______________________________
LA PARTE PROPIETARIA                          LA INMOBILIARIA — {{socia}}`,
  },
  {
    key: 'reserva',
    tipo: 'Boleto de reserva',
    titulo: 'Boleto de reserva',
    descripcion: 'Reserva del inmueble con entrega de seña por parte del comprador.',
    cuerpo: `BOLETO DE RESERVA

En la ciudad de {{ciudad}}, el {{fecha}}, {{cliente.nombre}}, documento de identidad [completar], domiciliado/a en [completar], en adelante "EL/LA OFERENTE", manifiesta su voluntad de adquirir el inmueble ubicado en {{propiedad.direccion}}, {{propiedad.ciudad}}, padrón número [completar], en las siguientes condiciones:

1. PRECIO OFRECIDO: {{propiedad.precio}}, pagadero de la siguiente forma: [completar].

2. RESERVA: En este acto EL/LA OFERENTE entrega a REVELA, en carácter de depositaria, la suma de USD [completar] como reserva, que se imputará al precio en caso de aceptación.

3. PLAZO DE ACEPTACIÓN: La parte vendedora tendrá [completar] días hábiles para aceptar esta oferta. Si no la acepta en ese plazo, la reserva será devuelta íntegramente a EL/LA OFERENTE.

4. PROMESA Y ESCRITURA: Aceptada la oferta, las partes suscribirán compromiso de compraventa dentro de [completar] días, y la escritura definitiva en la fecha que se acuerde, ante el/la escribano/a [completar].

5. DESISTIMIENTO: Si aceptada la oferta EL/LA OFERENTE desistiera de la operación, perderá la reserva entregada. Si desistiera la parte vendedora, deberá devolver la reserva [completar].

6. COMISIÓN: [completar] % más IVA a cargo de [completar].

7. CONDICIONES ESPECIALES: [completar].

Acompaña la operación: {{socia}} — REVELA.


______________________________          ______________________________
EL/LA OFERENTE                                     ACEPTACIÓN DE LA PARTE VENDEDORA
                                                              Fecha: ____ / ____ / ______`,
  },
  {
    key: 'arrendamiento',
    tipo: 'Arrendamiento',
    titulo: 'Contrato de arrendamiento',
    descripcion: 'Alquiler de vivienda con garantía, plazo y reajuste.',
    cuerpo: `CONTRATO DE ARRENDAMIENTO

En la ciudad de {{ciudad}}, el {{fecha}}, comparecen:

POR UNA PARTE: [completar], documento de identidad [completar], en adelante "LA PARTE ARRENDADORA".

POR OTRA PARTE: {{cliente.nombre}}, documento de identidad [completar], en adelante "LA PARTE ARRENDATARIA".

Y CONVIENEN:

PRIMERO (Objeto). LA PARTE ARRENDADORA da en arrendamiento a LA PARTE ARRENDATARIA el inmueble ubicado en {{propiedad.direccion}}, {{propiedad.ciudad}}, padrón número [completar], que será destinado exclusivamente a vivienda.

SEGUNDO (Plazo). El plazo es de [completar] meses a partir del [completar].

TERCERO (Precio). El precio mensual es de [completar], pagadero por adelantado del 1 al 10 de cada mes mediante [completar].

CUARTO (Reajuste). El precio se reajustará cada [completar] meses según [completar: índice o criterio acordado].

QUINTO (Garantía). LA PARTE ARRENDATARIA presenta como garantía: [completar: seguro de alquiler, depósito, garantía de fiel cumplimiento u otra].

SEXTO (Gastos y tributos). Serán de cargo de LA PARTE ARRENDATARIA: gastos comunes ordinarios, consumos de servicios y [completar]. Serán de cargo de LA PARTE ARRENDADORA: contribución inmobiliaria, impuesto de primaria y gastos extraordinarios.

SÉPTIMO (Estado y conservación). LA PARTE ARRENDATARIA recibe el inmueble en el estado que se describe en el inventario anexo y se obliga a devolverlo en igual estado, salvo el desgaste por el uso normal.

OCTAVO (Prohibiciones). Queda prohibido subarrendar, ceder el contrato o cambiar el destino sin autorización escrita.

NOVENO (Administración). La gestión del arrendamiento estará a cargo de REVELA, representada por {{socia}}.

DÉCIMO (Domicilios y notificaciones). Las partes constituyen domicilios especiales en los indicados y aceptan las notificaciones por correo electrónico: [completar].

Para constancia se firman [completar] ejemplares de igual tenor.


______________________________          ______________________________
LA PARTE ARRENDADORA                          LA PARTE ARRENDATARIA`,
  },
]

export function fillTemplate(body: string, ctx: { cliente?: Rec | null; propiedad?: Rec | null; socia?: string; ciudad?: string }) {
  const values: Record<string, string | undefined> = {
    fecha: fmtDayLong(today()),
    ciudad: ctx.ciudad || ctx.propiedad?.ciudad || 'Paysandú',
    socia: ctx.socia,
  }
  for (const [k, v] of Object.entries(ctx.cliente ?? {})) values[`cliente.${k}`] = v == null ? undefined : String(v)
  for (const [k, v] of Object.entries(ctx.propiedad ?? {})) values[`propiedad.${k}`] = v == null ? undefined : String(v)
  if (ctx.propiedad?.precio) values['propiedad.precio'] = usd(ctx.propiedad.precio)
  return body.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, k) => (values[k] && values[k] !== '' ? values[k]! : '[completar]'))
}
